import {ConfigManager, configHook} from '@pexip/config-manager';
import type {MediaDeviceInfoLike} from '@pexip/media-control';
import {currentBrowserName, isMobileDevice} from '@pexip/media-components';

import {
    BANDWIDTHS,
    INITIAL_BANDWIDTH,
    RENDER_EFFECTS,
    FRAME_RATE,
    FOREGROUND_THRESHOLD,
    BACKGROUND_BLUR_AMOUNT,
    EDGE_BLUR_AMOUNT,
    FLIP_HORIZONTAL,
    SEGMENTATION_MODEL,
    BG_IMAGE_URL,
    TERMS_URL,
} from './constants';

export const shouldEnableVideoProcessing = () =>
    !isMobileDevice() &&
    (currentBrowserName === 'Chrome' ||
        currentBrowserName === 'Firefox' ||
        currentBrowserName === 'Edge Chromium') &&
    (() => {
        const WEBGL_ATTRIBUTES = {
            alpha: false,
            antialias: false,
            premultipliedAlpha: false,
            preserveDrawingBuffer: false,
            depth: false,
            stencil: false,
            failIfMajorPerformanceCaveat: true,
        };
        try {
            const canvas = document.createElement('canvas');
            return !!(
                window.WebGLRenderingContext &&
                (canvas.getContext('webgl', WEBGL_ATTRIBUTES) ||
                    canvas.getContext('experimental-webgl', WEBGL_ATTRIBUTES))
            );
        } catch (e) {
            return false;
        }
    })();

const internalConfig = {
    asd: false,
    audioInput: {} as MediaDeviceInfoLike | undefined,
    audioOutput: {} as MediaDeviceInfoLike | undefined,
    lastFallBackMsg: '',
    showLayoutChangeConfirmationModal: true,
    showSelfviewHiddenTooltip: true,
    vad: false,
    videoInput: {} as MediaDeviceInfoLike | undefined,
};

const applicationConfig = {
    audioProcessing: true,
    bandwidths: BANDWIDTHS,
    frameRate: FRAME_RATE,
    node: window.location.host,
    segmentationModel: SEGMENTATION_MODEL,
    showLiveCaptionsFeature: true,
    videoProcessing: shouldEnableVideoProcessing(),
    shouldMaskConference: false,
    directMedia: true,
    termsAndConditions: {
        en: TERMS_URL,
    } as Record<string, string>,
    disconnectDestination: '',
    bgImageAssets: [BG_IMAGE_URL],
};
export type ApplicationConfig = typeof applicationConfig;

const defaultUserConfig = {
    backgroundBlurAmount: BACKGROUND_BLUR_AMOUNT,
    bandwidth: INITIAL_BANDWIDTH,
    bgImageUrl: BG_IMAGE_URL,
    denoise: false,
    displayName: '',
    edgeBlurAmount: EDGE_BLUR_AMOUNT,
    flipHorizontal: FLIP_HORIZONTAL,
    foregroundThreshold: FOREGROUND_THRESHOLD,
    isAudioInputMuted: false,
    isVideoInputMuted: false,
    segmentationEffects: RENDER_EFFECTS,
    showSelfView: true,
    isUserFacing: true,
};
export type DefaultUserConfig = typeof defaultUserConfig;

export const config = new ConfigManager(
    {
        ...internalConfig,
        ...applicationConfig,
        ...defaultUserConfig,
    },
    'infinity-connect',
);

export const useConfig = configHook(config);
